import { useState } from "react";
import { Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import { DataStructureFormProps, registerForm } from "./FormRegistry";
import { IDataEntity } from "services/backend/DataSchemaService";

export interface PlayerPrestigeData extends IDataEntity {
  currentLevel: number;
  currentXp: number;
  lifetimeXpEarned: number;
  nextLevelXp: number;
}

const PlayerPrestigeForm = ({ data, onSave, onCancel }: DataStructureFormProps<PlayerPrestigeData>) => {
  const [currentLevel, setCurrentLevel] = useState<number>(data.currentLevel ?? 0);
  const [currentXp, setCurrentXp] = useState<number>(data.currentXp ?? 0);
  const [currentLevelError, setCurrentLevelError] = useState<string>("");
  const [currentXpError, setCurrentXpError] = useState<string>("");

  const validateNumber = (value: number, fieldName: "currentLevel" | "currentXp"): boolean => {
    if (isNaN(value) || value < 0) {
      if (fieldName === "currentLevel") {
        setCurrentLevelError("Must be a positive number");
      } else {
        setCurrentXpError("Must be a positive number");
      }
      return false;
    }
    if (fieldName === "currentLevel") {
      setCurrentLevelError("");
    } else {
      setCurrentXpError("");
    }
    return true;
  };

  const handleSave = async () => {
    const isLevelValid = validateNumber(currentLevel, "currentLevel");
    const isXpValid = validateNumber(currentXp, "currentXp");

    if (!isLevelValid || !isXpValid) {
      return;
    }

    const updatedData: PlayerPrestigeData = {
      ...data,
      currentLevel,
      currentXp,
    };

    const success = await onSave(updatedData);
    if (success) {
      onCancel();
    }
  };

  return (
    <Paper sx={{ width: "100%", p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Edit Player Prestige
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Player Prestige ID: {data.id}
          </Typography>
        </Grid>
        <Grid size={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Version: {data.version}
          </Typography>
        </Grid>
        <Grid size={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Lifetime XP Earned: {data.lifetimeXpEarned}
          </Typography>
        </Grid>
        <Grid size={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Next Level XP: {data.nextLevelXp}
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Current Level"
            type="number"
            value={currentLevel}
            onChange={(e) => setCurrentLevel(Number(e.target.value))}
            error={!!currentLevelError}
            helperText={currentLevelError}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            fullWidth
            label="Current XP"
            type="number"
            value={currentXp}
            onChange={(e) => setCurrentXp(Number(e.target.value))}
            error={!!currentXpError}
            helperText={currentXpError}
          />
        </Grid>

        <Grid size={12}>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save Changes
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

registerForm("PlayerPrestige", PlayerPrestigeForm);
export default PlayerPrestigeForm;
